import { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from "@azure/msal-react";
import { loginRequest } from '../config';

/**
 * Custom hook for handling authentication state and actions
 * Fetches user profile and photo from Microsoft Graph after login
 * @returns {Object} Authentication state and methods
 */
export const useAuth = () => {
  const { instance, accounts } = useMsal();
  const [user, setUser] = useState(null);
  const photoUrlRef = useRef(null); // For cleanup

  useEffect(() => {
    const fetchProfile = async (accessToken) => {
      try {
        // Fetch user profile
        const profileRes = await fetch('https://graph.microsoft.com/v1.0/me', {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (!profileRes.ok) throw new Error(`Graph API returned ${profileRes.status}`);
        const profile = await profileRes.json();
        const cleanName = profile.displayName.replace(/\s*\(.*\)\s*/, '').trim();
        const initials = getInitials(cleanName);

        // Fetch user photo as blob and convert to Object URL
        let avatarUrl = '';
        try {
          // Clean up previous object URL if any
          if (photoUrlRef.current) {
            URL.revokeObjectURL(photoUrlRef.current);
            photoUrlRef.current = null;
          }
          const photoRes = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
            headers: { Authorization: `Bearer ${accessToken}` }
          });
          if (photoRes.ok) {
            const blob = await photoRes.blob();
            avatarUrl = URL.createObjectURL(blob);
            photoUrlRef.current = avatarUrl;
          }
        } catch (photoErr) {
          // No photo or error
        }

        setUser({
          name: profile.displayName,
          email: profile.mail || profile.userPrincipalName,
          role: profile.jobTitle || '',
          avatarUrl,
          initials
        });
      } catch (e) {
        // fallback to MSAL account info if Graph fails
        const fallbackName = accounts[0].name;
        const fallbackInitials = getInitials(fallbackName);
        setUser({
          name: fallbackName,
          email: accounts[0].username,
          role: '',
          avatarUrl: '',
          initials: fallbackInitials
        });
      }
    };

    if (accounts.length > 0) {
      instance.acquireTokenSilent(loginRequest)
        .then(response => fetchProfile(response.accessToken))
        .catch(() => {
          const fallbackName = accounts[0].name;
          const fallbackInitials = getInitials(fallbackName);
          setUser({
            name: fallbackName,
            email: accounts[0].username,
            role: '',
            avatarUrl: '',
            initials: fallbackInitials
          });
        });
    } else {
      setUser(null);
    }
    // Cleanup photo object URL on unmount
    return () => {
      if (photoUrlRef.current) {
        URL.revokeObjectURL(photoUrlRef.current);
        photoUrlRef.current = null;
      }
    };
  }, [accounts, instance]);

  const login = useCallback(() => {
    return instance.loginRedirect(loginRequest).catch(e => {
      console.error('Login error:', e);
    });
  }, [instance]);

  const logout = useCallback(() => {
    return instance.logoutRedirect().catch(e => {
      console.error('Logout error:', e);
    });
  }, [instance]);

  return { user, login, logout };
};

function getInitials(name) {
  if (!name) return '';
  
  // Strip anything in parentheses before processing
  const cleanName = name.replace(/\s*\(.*\)\s*/, '').trim();
  
  const parts = cleanName.split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default useAuth;
